/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { DeletedDiffWidget } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { Decoration, EditorView } from '@atlaskit/editor-prosemirror/view';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles/constants';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { token } from '@atlaskit/tokens';

import type { EndExperience } from '../../../types/types';
import { useUpdateEditorHeight } from '../../hooks/useUpdateEditorHeight';
import type { InvokedFrom } from '../../utils/analytics/analyticsFlowTypes';
import { isPromptUpdatesEnabledNoExposure } from '../../utils/prompt-updates-enabled';
import {
	containInlineCommentPrompt,
	findScrollContainers,
	getAifcModalWidth,
	getModalLeft,
	getModalTop,
	getResizableOrBreakoutWrapper,
	isInlineCommentPromptContained,
	pauseScrollAnchoring,
	restoreScrollAnchoring,
} from './utils';
import type { PausedScrollAnchor } from './utils';

/**
 * the z-index logic is duplicated in generative-ai-modal/UnhandledError
 */

// Where do we get this magic number 500?
// - The lowest product z-index we need to be below is 500 from the Feedback Collector.
// As well as the Editor toolbar popup menu, which is also 500.
// This allows us to be above the floating toolbar, but below the feedback collector.
// +2 because tables floating toolbar is +1.
const MAX_Z_INDEX = 500;

// Where do we get this magic number 402?
// - The highest product z-index we need to be above is 401 from the floating toolbar*.
// *in Confluence and Bitbucket. It is 600px in Atlas & 511 in Jira, however that is a more niche case than the Feedback Collector,
// so this is a known trade-off until we have a better solution.
const MIN_Z_INDEX = 402;

// Sets the z-index so it is not greater than the maximum, and less than the minimum.
const akAiModalRegionZIndex = Math.min(
	MAX_Z_INDEX,
	Math.max(MIN_Z_INDEX, akEditorFloatingPanelZIndex + 2),
);

enum MODAL_PINNING {
	TOP = 1,
	NONE = 0,
	BOTTOM = -1,
}

const slideFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(1rem)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const modalStyles = css({
	marginTop: token('space.200'),
	position: 'absolute',
	zIndex: akAiModalRegionZIndex,
	marginRight: token('space.negative.200'),
	marginLeft: token('space.negative.200'),
	animationName: slideFadeIn,
	animationDuration: '0.2s',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'forwards',
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const pinnedTopStyles = css({ position: 'fixed', top: 0 });

const pinnedBottomStyles = css({ position: 'fixed', bottom: 16, top: 'unset' });

interface ModalRegionProps {
	appearance: EditorAppearance;
	autoScroll?: boolean;
	children: ReactNode;
	decoration: Decoration;
	decorationSet: DecorationSet;
	editorRelativeWrapper?: Element | null;
	editorView: EditorView;
	endExperience: EndExperience;
	/**
	 * show-diff's `getDeletedWidgets` action, used to position the modal below deleted diff content.
	 * Optional so callers without the show-diff plugin (or before it loads) simply skip the offset.
	 */
	getDeletedWidgets?: (range?: { from: number; to: number }) => DeletedDiffWidget[];
	isDirectionalPinningEnabled?: boolean;
	/**
	 * Whether AI is streaming into the editor, from `aiExperience` shared state so it covers
	 * both streaming plugins.
	 */
	isStreaming?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	useFullEditorWidthForUnifiedBar?: boolean;
}

const getModalDecorationElement = (editorView: EditorView) => {
	return (
		editorView?.dom.querySelector<HTMLElement>('.ai-modal-mount-point-widget') ||
		editorView?.dom.querySelector<HTMLElement>('.ai-selection-node')
	);
};

export const ModalRegion = ({
	isDirectionalPinningEnabled,
	children,
	decoration,
	editorView,
	editorRelativeWrapper,
	appearance,
	endExperience,
	lastTriggeredFrom,
	decorationSet,
	getDeletedWidgets,
	isStreaming = false,
	useFullEditorWidthForUnifiedBar = false,
}: ModalRegionProps): JSX.Element | null => {
	// Ignored via go/ees005
	// eslint-disable-next-line @atlaskit/editor/no-as-casting
	const editorViewElement = editorView.dom as HTMLElement;

	const modalRef = useRef<HTMLDivElement>(null);
	// Tracks the last vertical position so we can replay the entry fade ONLY when the
	// modal actually moves rows (e.g. an undo/redo that shifts the anchor). Undo/redo
	// re-anchors without remounting, so the mount animation would not otherwise play.
	const prevModalTopRef = useRef<number | null>(null);
	// Highest top seen during the current comment-appearance stream; null when not streaming.
	const streamingMaxTopRef = useRef<number | null>(null);
	// Scroll containers whose scroll anchoring is paused for the current comment stream.
	const pausedAnchorRef = useRef<PausedScrollAnchor[] | null>(null);
	// Whether the user left the editor's own scroll box at the bottom; null counts as at the bottom.
	const editorScrollBoxAtBottomRef = useRef<boolean | null>(null);
	const [modalLeft, setModalLeft] = useState<number | string>('');
	const [modalTop, setModalTop] = useState(0);
	const [modalWidth, setModalWidth] = useState(0);
	const [transition, setTransition] = useState('');
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const [isPinned, setIsPinned] = useState<MODAL_PINNING>(MODAL_PINNING.NONE);
	const [modalHeight, setModalHeight] = useState(0);
	// get nearest resizable wrapper or breakout container looking up from the ai decoration position
	const resizableOrBreakoutWrapper = useMemo(
		() => getResizableOrBreakoutWrapper(editorView, decoration.to),
		[editorView, decoration],
	);

	const modalDecorationElement = getModalDecorationElement(editorView);
	const layoutColumn = modalDecorationElement?.closest<HTMLElement>('[data-layout-column]');
	const isColumnRelative = Boolean(layoutColumn && !useFullEditorWidthForUnifiedBar);
	const isPromptUpdatesLayoutColumn = isColumnRelative && isPromptUpdatesEnabledNoExposure();

	// We only want modal pinning in full page experiences.
	const isModalPinningEnabled = ['full-page', 'full-width'].includes(appearance);

	// EDF-161: https://product-fabric.atlassian.net/browse/EDF-161
	// If the editor has a max-height set, we need to set position relative for .ak-editor-content-area
	useEffect(() => {
		if (appearance === 'comment') {
			// editorRelativeWrapper is the closest but can be null
			// fallback to modalDecorationElement if editorRelativeWrapper is null
			let editorContentArea: HTMLElement | null = null;
			if (editorRelativeWrapper) {
				editorContentArea =
					editorRelativeWrapper.querySelector<HTMLElement>('.ak-editor-content-area');
			} else if (modalDecorationElement) {
				editorContentArea = modalDecorationElement.closest<HTMLElement>('.ak-editor-content-area');
			}

			if (editorContentArea) {
				const computedStyle = getComputedStyle(editorContentArea);
				const editorHasMaxHeight = computedStyle.maxHeight !== 'none';
				const originalPosition = computedStyle.position;

				if (editorHasMaxHeight) {
					editorContentArea.style.position = 'relative';
				}

				return () => {
					if (editorHasMaxHeight && editorContentArea) {
						editorContentArea.style.position = originalPosition;
					}
				};
			}
		}
	}, [modalDecorationElement, appearance, editorRelativeWrapper]);

	// Each stream ratchets from the bar's current position. Reset when a stream starts so a
	// second stream in the same session does not inherit the previous stream's lowest top.
	// Declared before the positioning effect so the reset runs first.
	useEffect(() => {
		if (isStreaming) {
			streamingMaxTopRef.current = null;
		}
	}, [isStreaming]);

	// listen for editor resize and update modal size and position
	useEffect(() => {
		// Comment editors that scroll inside their own box: keep it at the bottom while streaming so
		// the loading bar stays in view, unless the user scrolls away.
		const [nearestScrollContainer] =
			appearance === 'comment' &&
			UNSAFE_expValNoExposure('jira_ai_description_stream_stable_layout', 'isEnabled', false)
				? findScrollContainers(editorViewElement)
				: [];
		const editorScrollBox =
			nearestScrollContainer?.classList.contains('akEditor') ||
			nearestScrollContainer?.classList.contains('ak-editor-content-area')
				? nearestScrollContainer
				: undefined;
		let stickToBottomFrame: number | undefined;
		let lastUserScrollInput = 0;
		const onUserScrollInput = () => {
			lastUserScrollInput = performance.now();
		};
		const onEditorScrollBoxScroll = () => {
			// Only scrolls that follow user input count; layout and our own scrolling are ignored.
			if (!editorScrollBox || performance.now() - lastUserScrollInput > 500) {
				return;
			}
			editorScrollBoxAtBottomRef.current =
				editorScrollBox.scrollHeight - editorScrollBox.clientHeight - editorScrollBox.scrollTop <=
				2;
		};
		let unbindEditorScrollBox: (() => void) | undefined;
		if (editorScrollBox) {
			unbindEditorScrollBox = bindAll(editorScrollBox, [
				{ type: 'scroll', listener: onEditorScrollBoxScroll, options: { passive: true } },
				{ type: 'wheel', listener: onUserScrollInput, options: { passive: true } },
				{ type: 'touchmove', listener: onUserScrollInput, options: { passive: true } },
				{ type: 'keydown', listener: onUserScrollInput, options: { passive: true } },
				{ type: 'pointerdown', listener: onUserScrollInput, options: { passive: true } },
			]);
		}

		const setModalPosition = () => {
			if (!modalRef.current || !modalDecorationElement || !modalDecorationElement.offsetParent) {
				// The modalDecorationElement.offsetParent is used when calculating the modal top postion. If it is null
				// the modal will flikcer from top to bottom, because the top position resets to 0.
				return;
			}

			const positionReferenceElement =
				isColumnRelative && layoutColumn ? layoutColumn : editorViewElement;

			const top = getModalTop({
				appearance,
				modalElement: modalRef.current,
				editorRelativeWrapper,
				modalDecorationElement,
				lastTriggeredFrom,
				editorView,
				getDeletedWidgets,
			});
			const left = getModalLeft({
				relativePositionElement: positionReferenceElement,
				modalElement: modalRef.current,
				appearance,
				positioningBoundsElement: isPromptUpdatesLayoutColumn ? editorViewElement : undefined,
			});

			// Replace-mode streams (e.g. Improve description) keep the unreplaced original text
			// below the AI text until the final commit, so the document bottom rises and falls
			// chunk to chunk. While streaming, only let the bar move down so it stays below all
			// text without bouncing; it settles to the real bottom once streaming ends.
			let nextTop = top;
			// The experiment is read last so exposure only logs for comment editors that stream.
			if (
				appearance === 'comment' &&
				isStreaming &&
				isExperimentEnabled('jira_ai_description_stream_stable_layout')
			) {
				nextTop = Math.max(top, streamingMaxTopRef.current ?? top);
				streamingMaxTopRef.current = nextTop;
				// Write the position in this frame too; the state update renders a frame later.
				modalRef.current.style.top = `${nextTop}px`;
				// Browser scroll anchoring scrolls the container by every line the stream
				// adds above the viewport, moving the whole box. Pause it for the stream.
				if (!pausedAnchorRef.current) {
					pausedAnchorRef.current = pauseScrollAnchoring(editorViewElement);
				}
				if (editorScrollBox && editorScrollBoxAtBottomRef.current !== false) {
					if (stickToBottomFrame !== undefined) {
						cancelAnimationFrame(stickToBottomFrame);
					}
					stickToBottomFrame = requestAnimationFrame(() => {
						stickToBottomFrame = undefined;
						editorScrollBox.scrollTop = editorScrollBox.scrollHeight - editorScrollBox.clientHeight;
					});
				}
			} else {
				streamingMaxTopRef.current = null;
				restoreScrollAnchoring(pausedAnchorRef);
			}

			setModalLeft(left);
			setModalTop(nextTop);
			setModalWidth(
				isPromptUpdatesLayoutColumn
					? editorViewElement.offsetWidth
					: positionReferenceElement.offsetWidth,
			);
			//setting width to be animated here to make animation happen only after first positioning
			if (!transition) {
				setTransition('left 0.1s ease-in, left 0.1s ease-in');
			}
		};
		const restoreInlineCommentContainment = containInlineCommentPrompt(
			appearance,
			editorViewElement,
		);
		setModalPosition();
		const setModalPositionDebounced = debounce(() => {
			setModalPosition();
		}, 20);
		// While a comment editor streams, reposition in the same frame as the resize. The
		// debounced update leaves a few frames where the text has grown but the modal and the
		// held box height have not caught up.
		const resizeObserver = new ResizeObserver(() => {
			if (
				appearance === 'comment' &&
				isStreaming &&
				isExperimentEnabled('jira_ai_description_stream_stable_layout')
			) {
				setModalPositionDebounced.cancel();
				setModalPosition();
				return;
			}
			setModalPositionDebounced();
		});

		resizeObserver.observe(editorViewElement);

		if (layoutColumn) {
			resizeObserver.observe(layoutColumn);
		}

		// editorViewOffsetParent can be undefined when editor is set to display: none
		// (when Confluence switches to Preview mode)
		const editorViewOffsetParent = editorViewElement.offsetParent;
		if (editorViewOffsetParent) {
			// This resize observer is intended to fix the position of the modal region
			// when the config panel (for extensions or templates) is opened/closed
			resizeObserver.observe(editorViewOffsetParent);
		}

		return () => {
			restoreInlineCommentContainment();
			restoreScrollAnchoring(pausedAnchorRef);
			unbindEditorScrollBox?.();
			if (stickToBottomFrame !== undefined) {
				cancelAnimationFrame(stickToBottomFrame);
			}
			resizeObserver.disconnect();
			//clearing any potentially running debounced reposition
			setModalPositionDebounced.cancel();
		};
		// no need to rerun due to transition change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		editorViewElement,
		modalDecorationElement,
		editorRelativeWrapper,
		resizableOrBreakoutWrapper,
		layoutColumn,
		isColumnRelative,
		isPromptUpdatesLayoutColumn,
		// Re-runs positioning after --content-width changes the rendered modal width.
		modalWidth,
		useFullEditorWidthForUnifiedBar,
		getDeletedWidgets,
		appearance,
		isStreaming,
	]);

	useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		if (!scrollParentRef.current) {
			const scrollParentEl = findOverflowScrollParent(modalRef.current) || null;
			if (!scrollParentEl) {
				return;
			}
			scrollParentRef.current = scrollParentEl;
		}

		const modalHeightObserver = new ResizeObserver(() => {
			if (!modalRef.current) {
				return;
			}
			setModalHeight(modalRef.current.offsetHeight);
		});
		modalHeightObserver.observe(modalRef.current);

		return () => {
			modalHeightObserver.disconnect();
		};
	}, []);

	useEffect(() => {
		if (
			modalHeight &&
			scrollParentRef.current &&
			modalDecorationElement &&
			isModalPinningEnabled &&
			isDirectionalPinningEnabled
		) {
			const scrollParent = scrollParentRef.current;
			const observer = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							setIsPinned(MODAL_PINNING.NONE);
						} else {
							// We could be reacting to an element which is no longer in the DOM because prosemirror has removed it,
							// so we need to check that the target is still the current target.
							// This is not ideal and can be costly, however it's only a performance issue during the inline streaming
							// experience, and should have little affect the overall editor experience.
							const currentTarget = getModalDecorationElement(editorView);

							if (entry.target !== currentTarget) {
								// It's possible for the interaction observer callback to be called early as soon as the prosemirror
								// widget decoration has been removed. This means the react vdom update lags behind and doesn't disconnect
								// until a bit latter.
								return;
							}

							// somehow, during streaming, the rootBounds and other fields in entry will have all 0, which is really weird!
							if (!entry.rootBounds || entry.rootBounds.height === 0) {
								const rect = modalDecorationElement.getBoundingClientRect();
								const scrollRect = scrollParent.getBoundingClientRect();
								setIsPinned(
									rect.bottom < scrollRect.top ? MODAL_PINNING.TOP : MODAL_PINNING.BOTTOM,
								);
							} else {
								setIsPinned(
									entry.boundingClientRect.bottom < entry.rootBounds.top
										? MODAL_PINNING.TOP
										: MODAL_PINNING.BOTTOM,
								);
							}
						}
					}
				},
				{
					root: scrollParentRef.current,
					/**
					 * We set the bottom root margin to account for the modal height (so that the modal does not block content),
					 * the top margin and bottom margin of the modal (16px each).
					 * We also use the modal decoration's height to figure out the location of the bottom of the mount point widget.
					 */
					rootMargin: `0px 0px -${
						modalHeight + 16 + 16 + modalDecorationElement.getBoundingClientRect().height
					}px 0px`,
					threshold: 0,
				},
			);

			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			observer.observe(modalDecorationElement);

			return () => {
				observer.disconnect();
			};
		}
	}, [
		modalHeight,
		editorView,
		modalDecorationElement,
		isModalPinningEnabled,
		isDirectionalPinningEnabled,
	]);

	// sync modal height with editor content area height
	useUpdateEditorHeight({
		modalElement: modalRef.current,
		editorViewElement,
		isInDocumentStreamingEnabled: true,
		isStreaming,
		modalTop,
		// Evaluated on every render, so read without exposure; the streaming checks above log it.
		holdHeightDuringStreaming:
			appearance === 'comment' &&
			UNSAFE_expValNoExposure('jira_ai_description_stream_stable_layout', 'isEnabled', false),
	});

	// Replay the entry fade when the modal MOVES to a new row without remounting
	// (undo/redo re-anchors in place). The first positioning is skipped so this does
	// not double up with the mount animation; an unchanged `modalTop` (e.g. an in-line
	// edit) does nothing, so the modal only fades when it actually moves.
	useEffect(() => {
		const el = modalRef.current;
		const prevTop = prevModalTopRef.current;
		prevModalTopRef.current = modalTop;
		// Skip the initial position and no-op updates (same row).
		if (el === null || prevTop === null || prevTop === modalTop) {
			return;
		}
		// A streaming comment moves the modal on every line; replaying the slide-in each
		// time makes it glide 1rem after each move.
		if (
			appearance === 'comment' &&
			isStreaming &&
			isExperimentEnabled('jira_ai_description_stream_stable_layout')
		) {
			return;
		}
		// Restart the CSS `slideFadeIn` animation: clear it, force a reflow, restore.
		el.style.animation = 'none';
		// Reading a layout property forces the browser to flush the removal so the
		// animation can re-trigger when it is restored below.
		void el.offsetHeight;
		el.style.animation = '';
	}, [modalTop, appearance, isStreaming]);

	// Check for the modal decoration which is used for positioning
	// If there is none the modal should be removed from the DOM & return early
	// This is intended to make the code more resilient to bugs caused by the
	// editor AI plugin not being reinstantiated correctly when the editor is closed/opened
	// See https://product-fabric.atlassian.net/browse/EDITOR-52

	if (decorationSet === DecorationSet.empty) {
		endExperience();
		return null;
	}

	const inlineCommentPromptContained = isInlineCommentPromptContained(appearance, editorView.dom);
	const style = {
		...((!isModalPinningEnabled ||
			(isDirectionalPinningEnabled && isPinned === MODAL_PINNING.NONE)) && {
			top: modalTop,
			transition: isPromptUpdatesLayoutColumn ? undefined : transition,
		}),
		// The usual width sticks out past an inline comment and opens a horizontal
		// scrollbar. Match the comment width instead.
		left: inlineCommentPromptContained ? 0 : modalLeft,
		width: inlineCommentPromptContained ? '100%' : getAifcModalWidth(appearance),
		...(inlineCommentPromptContained && { marginLeft: 0, marginRight: 0 }),
		// Prompt updates in layout columns keep their local anchor but use the editor
		// content width, so the updated AIFC treatment remains usable.
		'--content-width': `${modalWidth}px`,
		display: modalWidth ? 'block' : 'none',
	};

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className="ak-editor-plugin-ai-modal"
			data-testid="ai-plugin-modal"
			css={[
				modalStyles,
				isModalPinningEnabled &&
					isDirectionalPinningEnabled &&
					isPinned === MODAL_PINNING.TOP &&
					pinnedTopStyles,
				isModalPinningEnabled &&
					((isDirectionalPinningEnabled && isPinned === MODAL_PINNING.BOTTOM) ||
						!isDirectionalPinningEnabled) &&
					pinnedBottomStyles,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={style}
			ref={modalRef}
			// data-editor-popup is required so that clicks within the modal will be detected as clicks inside the editor,
			// Otherwise, content selection will be prevented in packages/editor/editor-core/src/ui/Addon/click-area-helper.ts
			data-editor-popup={true}
		>
			{children}
		</div>
	);
};
